import {Users} from 'lucide-react';
import {useIntl} from 'react-intl';
import {useGroupCallRoomName} from '../../features/groupCalls/hooks/useGroupCallRoomName';
import type {Call} from '../../store/features/calls/callsService';
import {formatCallHistoryTime} from '../../utilis/timeUtilis';
import {groupCallMessages as m} from '../../features/groupCalls/messages';
import {ParticipantAvatarStack} from '../../features/groupCalls/components/ParticipantAvatarStack';
import {groupHistoryMessages as hm} from './groupHistoryMessages';
import {
    groupCallDuration,
    groupCallJoinedParticipants,
    groupCallOutcomeMessage,
    groupCallParticipantName,
    isGroupCallOutcomeMissed,
} from '../../features/groupCalls/services/groupCallCallPresentation';

interface GroupCallRowProps {
    call: Call;
    isMobile: boolean;
    isSelected: boolean;
    onSelect: (call: Call) => void;
}

const GroupCallRow = ({call, isMobile, isSelected, onSelect}: GroupCallRowProps) => {
    const intl = useIntl();
    const roomName = useGroupCallRoomName(call.roomId);
    const missed = isGroupCallOutcomeMissed(call);
    const duration = groupCallDuration(call);
    const joined = groupCallJoinedParticipants(call).map(participant => ({
        displayName: groupCallParticipantName(participant) ?? intl.formatMessage(m.historyUnknownUser),
        avatarUrl: participant.avatarUrl,
    }));
    const participantCount = intl.formatMessage(m.callsParticipantCount, {count: call.participants?.length ?? 0});

    const time = formatCallHistoryTime(call.startedAt, new Date(), intl.formatMessage({id: 'time.yesterday'}));
    const details = [
        call.callIntent ? intl.formatMessage(hm.intentShort, {intent: call.callIntent}) : undefined,
        duration ?? participantCount,
    ].filter(Boolean).join(' · ');

    // Same Design System ListRow as a single call: 48px, 32px tile, one-line
    // subline, trailing faces and time.
    return (
        <div
            role="button"
            tabIndex={0}
            aria-current={isSelected ? 'true' : undefined}
            data-testid="calls-group-call-row"
            onClick={() => onSelect(call)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(call);
                }
            }}
            className={`flex cursor-pointer items-center gap-3 rounded-control px-2 outline-none focus-visible:ring-2 focus-visible:ring-brand-focus ${
                isMobile ? 'h-14' : 'h-12'
            } ${isSelected ? 'bg-brand-tint-subtle' : 'hover:bg-surface-muted'}`}
        >
            <span className={`inline-flex size-8 shrink-0 items-center justify-center rounded-control ${missed ? 'bg-danger-tint text-danger-ink' : 'bg-brand-tint-subtle text-brand-ink'}`}>
                <Users size={16} className="!p-0 !text-current" aria-hidden="true"/>
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-callout font-semibold text-ink">{roomName}</span>
                <span className="mt-0.5 truncate text-footnote text-ink-muted">
                    <span className={missed ? 'font-medium text-danger-ink' : ''}>{intl.formatMessage(groupCallOutcomeMessage(call))}</span>
                    {details && ` · ${details}`}
                </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
                {joined.length > 0 && <ParticipantAvatarStack participants={joined} compact/>}
                <span className={`text-caption ${missed ? 'text-danger-ink' : 'text-ink-muted'}`}>{time}</span>
            </div>
        </div>
    );
};

export default GroupCallRow;
